import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { T } from '@design/index';
import ApprovedPatientCard from '../components/ApprovedPatientCard';
import { loadPatientIdentity } from '../utils/patientIdentity';
import type { PatientIdentity } from '../utils/patientIdentity';
import { clearAuthContext } from '../../../api/http';
import { clearToken } from '../../../api/patientSession';
import Spinner from '../../../components/ui/Spinner';
import { GOV_COMPLIANCE, govPageContainer } from '../ui/PatientUIGovernor';
import { patientAuthApi } from '../api';

/* ─── Shared styles ─── */

const CARD: React.CSSProperties = {
  background: T.white,
  border: `1px solid ${T.border}`,
  borderRadius: 16,
  padding: 0,
  boxShadow: T.shadow,
  overflow: 'hidden',
};

const GROUP_TITLE: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  color: T.textTer,
  fontFamily: T.font,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  padding: '4px 0 0',
};

/* ─── Reusable row (TASK 5) ─── */

interface RowProps {
  icon: string;
  iconColor: string;
  label: string;
  subtitle: string;
  badge?: string;
  badgeColor?: string;
  route?: string;
  onClick?: () => void;
  isFirst?: boolean;
}

function ProfileRow({ icon, iconColor, label, subtitle, badge, badgeColor, route, onClick, isFirst }: RowProps) {
  const navigate = useNavigate();
  return (
    <button
      onClick={onClick || (() => route && navigate(route))}
      aria-label={`${label}: ${subtitle}`}
      style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', width: '100%',
        cursor: 'pointer', background: 'none', border: 'none',
        borderTop: isFirst ? 'none' : `1px solid ${T.borderLight}`,
        textAlign: 'left',
      }}
    >
      <div style={{
        width: 32, height: 32, borderRadius: 10,
        background: `${iconColor}10`, display: 'grid', placeItems: 'center', flexShrink: 0,
      }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          {icon.split(' M').map((seg, i) => <path key={i} d={i === 0 ? seg : `M${seg}`} />)}
        </svg>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: T.text, fontFamily: T.font, lineHeight: 1.3 }}>{label}</div>
        <div style={{ fontSize: 9.5, color: T.textTer, fontFamily: T.font, marginTop: 1 }}>{subtitle}</div>
      </div>
      {badge && (
        <span style={{
          fontSize: 7.5, fontWeight: 700, color: '#FFF',
          background: badgeColor || T.textTer,
          padding: '2px 7px', borderRadius: 4, fontFamily: T.font, flexShrink: 0,
          letterSpacing: '0.03em',
        }}>{badge}</span>
      )}
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.textTer} strokeWidth="2" strokeLinecap="round" style={{ flexShrink: 0 }}>
        <path d="M9 18l6-6-6-6" />
      </svg>
    </button>
  );
}

/* ─── SVG icon paths ─── */
const ICONS = {
  user: 'M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2 M12 3a4 4 0 100 8 4 4 0 000-8z',
  heart: 'M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z',
  pill: 'M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3',
  scissors: 'M6 9a3 3 0 100-6 3 3 0 000 6z M6 21a3 3 0 100-6 3 3 0 000 6z M20 4L8.12 15.88 M14.47 14.48L20 20 M8.12 8.12L12 12',
  family: 'M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2 M9 3a4 4 0 100 8 4 4 0 000-8z M23 21v-2a4 4 0 00-3-3.87 M16 3.13a4 4 0 010 7.75',
  phone: 'M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z',
  alert: 'M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z M12 9v4 M12 17h.01',
  ambulance: 'M7 17h.01 M17 17h.01 M5 17H3v-4l2-5h10l2 5v4h-2 M7 17a2 2 0 100-4 2 2 0 000 4z M17 17a2 2 0 100-4 2 2 0 000 4z',
  shield: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z',
  folder: 'M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z',
  clipboard: 'M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2 M8.5 2h7a.5.5 0 01.5.5v1a.5.5 0 01-.5.5h-7a.5.5 0 01-.5-.5v-1a.5.5 0 01.5-.5z',
  flask: 'M10 2v7.527a2 2 0 01-.211.896L4.72 20.18A1 1 0 005.597 22h12.806a1 1 0 00.877-1.82l-5.07-9.757A2 2 0 0114 9.527V2 M8.5 2h7',
  scan: 'M7 3H5a2 2 0 00-2 2v2 M17 3h2a2 2 0 012 2v2 M7 21H5a2 2 0 01-2-2v-2 M17 21h2a2 2 0 002-2v-2 M8 12h8',
  image: 'M5 3h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2z M8.5 10a1.5 1.5 0 100-3 1.5 1.5 0 000 3z M21 15l-5-5L5 21',
  certificate: 'M12 15l-2 5 2-1 2 1-2-5z M9 12a3 3 0 106 0 3 3 0 00-6 0z M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10z',
  building: 'M3 21h18 M3 7v14 M21 7v14 M5 7V4a1 1 0 011-1h4a1 1 0 011 1v3 M13 7V4a1 1 0 011-1h4a1 1 0 011 1v3 M9 21v-4a1 1 0 011-1h4a1 1 0 011 1v4',
  fileText: 'M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z M14 2v6h6 M16 13H8 M16 17H8 M10 9H8',
  clock: 'M12 2a10 10 0 100 20 10 10 0 000-20z M12 6v6l4 2',
  eye: 'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z M12 9a3 3 0 100 6 3 3 0 000-6z',
  lock: 'M19 11H5a2 2 0 00-2 2v7a2 2 0 002 2h14a2 2 0 002-2v-7a2 2 0 00-2-2z M7 11V7a5 5 0 0110 0v4',
  consent: 'M9 11l3 3L22 4 M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11',
  bell: 'M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9 M13.73 21a2 2 0 01-3.46 0',
  monitor: 'M2 3h20v14H2z M8 21h8 M12 17v4',
  calendar: 'M19 4H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V6a2 2 0 00-2-2z M16 2v4 M8 2v4 M3 10h18',
  doctor: 'M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z M9 22V12h6v10',
  video: 'M23 7l-7 5 7 5V7z M1 5h15v14H1z',
  followup: 'M1 4v6h6 M23 20v-6h-6 M20.49 9A9 9 0 005.64 5.64L1 10 M23 14l-4.64 4.36A9 9 0 013.51 15',
  cart: 'M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6 M9 22a1 1 0 100-2 1 1 0 000 2z M20 22a1 1 0 100-2 1 1 0 000 2z',
  refresh: 'M23 4v6h-6 M1 20v-6h6 M3.51 9a9 9 0 0114.85-3.36L23 10 M1 14l4.64 4.36A9 9 0 0020.49 15',
  settings: 'M12.22 2h-.44a2 2 0 00-2 2v.18a2 2 0 01-1 1.73l-.43.25a2 2 0 01-2 0l-.15-.08a2 2 0 00-2.73.73l-.22.38a2 2 0 00.73 2.73l.15.1a2 2 0 011 1.72v.51a2 2 0 01-1 1.74l-.15.09a2 2 0 00-.73 2.73l.22.38a2 2 0 002.73.73l.15-.08a2 2 0 012 0l.43.25a2 2 0 011 1.73V20a2 2 0 002 2h.44a2 2 0 002-2v-.18a2 2 0 011-1.73l.43-.25a2 2 0 012 0l.15.08a2 2 0 002.73-.73l.22-.39a2 2 0 00-.73-2.73l-.15-.08a2 2 0 01-1-1.74v-.5a2 2 0 011-1.74l.15-.09a2 2 0 00.73-2.73l-.22-.38a2 2 0 00-2.73-.73l-.15.08a2 2 0 01-2 0l-.43-.25a2 2 0 01-1-1.73V4a2 2 0 00-2-2z M12 8a4 4 0 100 8 4 4 0 000-8z',
  key: 'M21 2l-2 2m-7.61 7.61a5.5 5.5 0 11-7.778 7.778 5.5 5.5 0 017.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4',
  download: 'M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4 M7 10l5 5 5-5 M12 15V3',
  logout: 'M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4 M16 17l5-5-5-5 M21 12H9',
  trash: 'M3 6h18 M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2',
  insurance: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z M9 12l2 2 4-4',
  qr: 'M7 3H5a2 2 0 00-2 2v2 M17 3h2a2 2 0 012 2v2 M7 21H5a2 2 0 01-2-2v-2 M17 21h2a2 2 0 002-2v-2',
};

/* ─── Modal ─── */

function PlaceholderModal({ title, message, onClose }: { title: string; message: string; onClose: () => void }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.4)', display: 'grid', placeItems: 'center', padding: 24 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: T.white, borderRadius: 20, padding: 24, maxWidth: 340, width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: T.text, fontFamily: T.font, marginBottom: 8 }}>{title}</div>
        <div style={{ fontSize: 11, color: T.textSec, fontFamily: T.font, lineHeight: 1.6, marginBottom: 16, whiteSpace: 'pre-line' }}>{message}</div>
        <button onClick={onClose} style={{ width: '100%', padding: '10px 0', borderRadius: 10, border: 'none', background: T.teal, color: '#FFF', fontSize: 11, fontWeight: 700, fontFamily: T.font, cursor: 'pointer' }}>Got it</button>
      </div>
    </div>
  );
}

/* ─── Main ─── */

export default function PatientProfile() {
  const navigate = useNavigate();
  const [id, setId] = useState<PatientIdentity | null>(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<{ title: string; message: string } | null>(null);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  useEffect(() => {
    loadPatientIdentity()
      .then(({ identity }) => setId(identity))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !id) return <div className="flex justify-center py-20"><Spinner size="lg" /></div>;

  const handleLogout = () => {
    // Clears the JWT + auth context + patient summary — without these the
    // "logged out" user still carried a valid session in localStorage.
    clearAuthContext();
    clearToken();
    localStorage.removeItem('nhc_patient_onboarding');
    window.location.href = '/patient/entry';
  };

  return (
    <div style={{ ...govPageContainer(), display: 'flex', flexDirection: 'column', gap: 12 }}>

      {modal && <PlaceholderModal title={modal.title} message={modal.message} onClose={() => setModal(null)} />}
      {showLogoutConfirm && (
        <div onClick={() => setShowLogoutConfirm(false)} style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.4)', display: 'grid', placeItems: 'center', padding: 24 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: T.white, borderRadius: 20, padding: 24, maxWidth: 340, width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.text, fontFamily: T.font, marginBottom: 8 }}>Log Out?</div>
            <div style={{ fontSize: 11, color: T.textSec, fontFamily: T.font, lineHeight: 1.6, marginBottom: 16 }}>You will be signed out of your NeoHealthCard account on this device.</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setShowLogoutConfirm(false)} style={{ flex: 1, padding: '10px 0', borderRadius: 10, border: `1.5px solid ${T.border}`, background: 'transparent', color: T.text, fontSize: 11, fontWeight: 600, fontFamily: T.font, cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleLogout} style={{ flex: 1, padding: '10px 0', borderRadius: 10, border: 'none', background: T.danger, color: '#FFF', fontSize: 11, fontWeight: 700, fontFamily: T.font, cursor: 'pointer' }}>Log Out</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ TOP — Digital NeoHealthCard ═══ */}

      <button onClick={() => navigate('/patient/nhc-card')} aria-label="View Digital NeoHealthCard" style={{ width: '100%', display: 'flex', justifyContent: 'center', background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' }}>
        <ApprovedPatientCard
          name={id.patientName}
          nhcId={id.nhcIdDisplay}
          nhcIdRaw={id.nhcIdRaw}
          bloodGroup={id.bloodGroup}
          insuranceStatus={id.insuranceStatus}
        />
      </button>

      {/* Three primary actions */}
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={() => navigate('/patient/nhc-card')} style={{ flex: 1, padding: '10px 0', borderRadius: 10, border: 'none', background: T.teal, color: '#FFF', fontSize: 10, fontWeight: 700, fontFamily: T.font, cursor: 'pointer' }}>View QR / NFC</button>
        <button onClick={() => navigate('/patient/consent')} style={{ flex: 1, padding: '10px 0', borderRadius: 10, border: `1.5px solid ${T.teal}`, background: 'transparent', color: T.teal, fontSize: 10, fontWeight: 700, fontFamily: T.font, cursor: 'pointer' }}>Share with Provider</button>
        <button onClick={() => navigate('/patient/emergency')} style={{ flex: 1, padding: '10px 0', borderRadius: 10, border: `1.5px solid ${T.danger}`, background: 'transparent', color: T.danger, fontSize: 10, fontWeight: 700, fontFamily: T.font, cursor: 'pointer' }}>Emergency Card</button>
      </div>

      {/* Verification status strip */}
      <button onClick={() => navigate('/patient/profile/registration')} aria-label="View Registration & Verification" style={{
        display: 'flex', justifyContent: 'space-around', background: T.white, border: `1px solid ${T.border}`,
        borderRadius: 12, padding: '10px 8px', cursor: 'pointer', width: '100%',
      }}>
        {[
          { label: 'Phone', ok: id.phone !== 'Not set' },
          { label: 'Email', ok: id.email !== 'Not set' },
          { label: 'NeoVerify', ok: !!id.neoVerifyId },
          { label: 'ABHA / ABDM', ok: !!id.abhaId },
        ].map((s, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{ width: 6, height: 6, borderRadius: 3, background: s.ok ? T.success : T.warning }} />
            <span style={{ fontSize: 8.5, fontWeight: 600, color: s.ok ? T.success : T.warning, fontFamily: T.font }}>{s.label}</span>
          </div>
        ))}
      </button>

      {/* ═══ GROUP 1 — Personal & Medical Profile ═══ */}
      <div style={GROUP_TITLE}>Personal &amp; Medical Profile</div>
      <div style={CARD}>
        <ProfileRow icon={ICONS.user} iconColor={T.teal} label="Personal Details" subtitle="Name, DOB, gender, phone, address" route="/patient/settings" isFirst />
        <ProfileRow icon={ICONS.heart} iconColor={T.danger} label="Medical Basics" subtitle="Blood group, allergies, conditions" route="/patient/medical-basics" />
        <ProfileRow icon={ICONS.pill} iconColor={T.teal} label="Current Medicines" subtitle="Active medicines and reminders" route="/patient/medicines" />
        <ProfileRow icon={ICONS.scissors} iconColor={T.purple} label="Past Surgeries" subtitle="Surgery history" route="/patient/medical-basics" />
        <ProfileRow icon={ICONS.family} iconColor={T.navy} label="Family History" subtitle="Family disease history" route="/patient/medical-basics" />
      </div>

      {/* ═══ GROUP 2 — Emergency Profile ═══ */}
      <div style={GROUP_TITLE}>Emergency Profile</div>
      <div style={CARD}>
        <ProfileRow icon={ICONS.phone} iconColor={T.danger} label="Emergency Contact" subtitle="Who to call in emergency" route="/patient/emergency" isFirst />
        <ProfileRow icon={ICONS.alert} iconColor={T.danger} label="Emergency Health Summary" subtitle="Blood group, allergies, critical conditions" route="/patient/emergency" />
        <ProfileRow icon={ICONS.ambulance} iconColor={T.danger} label="Ambulance Booking" subtitle="Book emergency ambulance" route="/patient/care-services/ambulance" />
        <ProfileRow icon={ICONS.eye} iconColor={T.warning} label="Emergency Access" subtitle="Emergency record access and logs" route="/patient/access-logs" />
      </div>

      {/* ═══ GROUP 3 — Health Records ═══ */}
      <div style={GROUP_TITLE}>Health Records</div>
      <div style={CARD}>
        <ProfileRow icon={ICONS.folder} iconColor={T.info} label="Health Vault" subtitle="All medical documents" route="/patient/health-vault" isFirst />
        <ProfileRow icon={ICONS.clipboard} iconColor={T.teal} label="Prescriptions" subtitle="Doctor prescriptions and medicine orders" route="/patient/prescriptions" />
        <ProfileRow icon={ICONS.flask} iconColor={T.purple} label="Lab Reports" subtitle="Blood, urine, pathology reports" route="/patient/lab-reports" />
        <ProfileRow icon={ICONS.image} iconColor="#6366F1" label="Diagnostics & Imaging" subtitle="X-ray, CT, MRI, ultrasound, scans" route="/patient/diagnostics" />
        <ProfileRow icon={ICONS.certificate} iconColor={T.success} label="Vaccination Certificates" subtitle="Immunization records" route="/patient/certificates" />
        <ProfileRow icon={ICONS.building} iconColor={T.navy} label="Hospital Documents" subtitle="Discharge, OPD, IPD documents" route="/patient/documents" />
        <ProfileRow icon={ICONS.insurance} iconColor={T.teal} label="Insurance Documents" subtitle="Policy, claim, preauth documents" route="/patient/care-services/insurance" />
      </div>

      {/* ═══ GROUP 4 — Timeline & Access ═══ */}
      <div style={GROUP_TITLE}>Timeline &amp; Access</div>
      <div style={CARD}>
        <ProfileRow icon={ICONS.clock} iconColor={T.teal} label="Complete Health Timeline" subtitle="Visits, labs, medicines, ambulance, hospital events" route="/patient/timeline" isFirst />
        <ProfileRow icon={ICONS.eye} iconColor={T.warning} label="Provider Access History" subtitle="Who viewed your records" route="/patient/access-logs" />
        <ProfileRow icon={ICONS.consent} iconColor={T.success} label="Consent Activity" subtitle="Shared and revoked records" route="/patient/consent" />
        <ProfileRow icon={ICONS.shield} iconColor={T.navy} label="Access & Verification Logs" subtitle="Server-verified access entries to your records" route="/patient/access-logs" />
      </div>

      {/* ═══ GROUP 5 — Care & Appointments ═══ */}
      <div style={GROUP_TITLE}>Care &amp; Appointments</div>
      <div style={CARD}>
        <ProfileRow icon={ICONS.calendar} iconColor={T.teal} label="My Appointments" subtitle="Upcoming and past appointments" route="/patient/appointments" isFirst />
        <ProfileRow icon={ICONS.doctor} iconColor={T.navy} label="Book Doctor" subtitle="Find and book doctors" route="/patient/care-services/doctors" />
        <ProfileRow icon={ICONS.building} iconColor={T.info} label="Book Hospital / OPD" subtitle="Hospital visit and OPD booking" route="/patient/care-services/hospitals" />
        <ProfileRow icon={ICONS.flask} iconColor={T.purple} label="Book Lab Test" subtitle="Lab booking and home collection" route="/patient/care-services/labs" />
        <ProfileRow icon={ICONS.scan} iconColor="#6366F1" label="Diagnostics / Imaging Booking" subtitle="Scan and imaging booking" route="/patient/care-services/imaging" />
        <ProfileRow icon={ICONS.video} iconColor={T.info} label="Telemedicine" subtitle="Video, audio, and care chat" route="/patient/care-services/telemedicine" />
        <ProfileRow icon={ICONS.followup} iconColor={T.warning} label="Follow-ups" subtitle="Post-visit reminders" route="/patient/follow-ups" />
      </div>

      {/* ═══ GROUP 6 — Orders & Medicines ═══ */}
      <div style={GROUP_TITLE}>Orders &amp; Medicines</div>
      <div style={CARD}>
        <ProfileRow icon={ICONS.cart} iconColor="#059669" label="Pharmacy Orders" subtitle="Medicine orders and delivery" route="/patient/care-services/pharmacy" isFirst />
        <ProfileRow icon={ICONS.bell} iconColor={T.teal} label="Medicine Reminders" subtitle="Dose schedule, before/after food" route="/patient/medicines" />
        <ProfileRow icon={ICONS.refresh} iconColor={T.navy} label="Refills" subtitle="Repeat medicines and refill requests" route="/patient/care-services/pharmacy" />
      </div>

      {/* ═══ GROUP 7 — Consent, Privacy & Security ═══ */}
      <div style={GROUP_TITLE}>Consent, Privacy &amp; Security</div>
      <div style={CARD}>
        <ProfileRow icon={ICONS.consent} iconColor={T.success} label="Consent & Sharing" subtitle="Manage shared records" route="/patient/consent" isFirst />
        <ProfileRow icon={ICONS.lock} iconColor={T.danger} label="Security Settings" subtitle="Password, OTP, sessions" route="/patient/security" />
        <ProfileRow icon={ICONS.bell} iconColor="#6366F1" label="Notifications" subtitle="Medicine, report, appointment alerts" route="/patient/notifications" />
        <ProfileRow icon={ICONS.shield} iconColor={T.navy} label="Privacy Controls" subtitle="Data sharing and visibility" route="/patient/security" />
        <ProfileRow icon={ICONS.monitor} iconColor={T.textTer} label="Active Sessions & Devices" subtitle="See signed-in devices · revoke any of them" route="/patient/security/sessions" />
        <ProfileRow icon={ICONS.lock} iconColor={T.navy} label="App Lock" subtitle="PIN and biometric device protection" route="/patient/profile/app-lock" />
      </div>

      {/* ═══ GROUP 8 — Family & Insurance ═══ */}
      <div style={GROUP_TITLE}>Family &amp; Insurance</div>
      <div style={CARD}>
        <ProfileRow icon={ICONS.family} iconColor={T.navy} label="Family Profiles" subtitle="Dependents and linked family" route="/patient/family" isFirst />
        <ProfileRow icon={ICONS.insurance} iconColor={T.success} label="Insurance Card" subtitle="Policy status and coverage" route="/patient/care-services/insurance" />
        <ProfileRow icon={ICONS.fileText} iconColor={T.teal} label="Claims / Preauth" subtitle="Claim and approval status" route="/patient/care-services/insurance" />
        <ProfileRow icon={ICONS.bell} iconColor={T.warning} label="Renewal Reminder" subtitle="Policy renewal alerts" route="/patient/care-services/insurance" />
      </div>

      {/* ═══ GROUP 9 — Account ═══ */}
      <div style={GROUP_TITLE}>Account</div>
      <div style={CARD}>
        <ProfileRow icon={ICONS.settings} iconColor={T.textSec} label="Edit Profile" subtitle="Update personal information" route="/patient/settings" isFirst />
        <ProfileRow icon={ICONS.key} iconColor={T.navy} label="Change Password" subtitle="Account security" route="/patient/security" />
        <ProfileRow icon={ICONS.download} iconColor={T.teal} label="Download Card" subtitle="Open your card, then use Print / Save as PDF" route="/patient/nhc-card" />
        <ProfileRow icon={ICONS.shield} iconColor={T.navy} label="Legal & Health Safety" subtitle="Terms, privacy, disclaimers" route="/patient/profile/legal" />
      </div>

      <button onClick={() => setShowLogoutConfirm(true)} aria-label="Log Out" style={{
        width: '100%', padding: '11px 0', borderRadius: 12, border: `1.5px solid ${T.danger}`,
        background: 'transparent', color: T.danger, fontSize: 11, fontWeight: 700, fontFamily: T.font, cursor: 'pointer',
      }}>Log Out</button>

      <button onClick={async () => {
        // Real request — recorded on the account (audit log + notification);
        // actual deletion is processed manually by support for now.
        if (!confirm('Record an account deletion request? Support will contact you on your registered phone/email before anything is removed. You can keep using the app meanwhile.')) return;
        try {
          const res = await patientAuthApi.requestAccountDeletion();
          setModal({
            title: res.alreadyPending ? 'Request already on record' : 'Deletion request recorded',
            message: res.alreadyPending
              ? `A deletion request from ${new Date(res.requestedAt).toLocaleDateString('en-IN')} is already on your account. Support will contact you before anything is removed.`
              : 'Your deletion request has been recorded on your NeoHealthCard account. Deletion is currently processed by the support team — they will contact you on your registered phone/email before removing anything.',
          });
        } catch {
          setModal({ title: 'Request failed', message: 'The server could not be reached — no deletion request was recorded. Please try again.' });
        }
      }} aria-label="Request Account Deletion" style={{
        width: '100%', padding: '11px 0', borderRadius: 12, border: `1.5px solid ${T.border}`,
        background: 'transparent', color: T.textTer, fontSize: 11, fontWeight: 600, fontFamily: T.font, cursor: 'pointer',
      }}>Request Account Deletion</button>

      <div style={{ ...GOV_COMPLIANCE.text, marginTop: 12, paddingBottom: 12 }}>
        NeoHealthCard™ · Your health identity is secured · Every access is audit logged
      </div>
    </div>
  );
}
